import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useInvoicesQuery } from '../api';
import { InvoiceTable } from '../components/InvoiceTable';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

/**
 * Sprint 15 — the merchant's invoices. Same posture as `ShipmentsPage`
 * / `OrderListPage`: no client-side gate on `orders:write` before the
 * first fetch — a real 403 is surfaced like any other query error.
 *
 * Every invoice here is created automatically at checkout. The "Non
 * fiscale" badge (OQ-04) is honest, not a placeholder: the fiscal fields
 * stay off until an accountant validates the Tunisian rules.
 */
export function InvoiceListPage(): ReactElement {
  const { t } = useI18n();
  const invoicesQuery = useInvoicesQuery();

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="sk-page-title">{t('merchant.nav.invoices')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.invoices.subtitle')}</p>
      </div>

      {invoicesQuery.isPending ? (
        <TableSkeleton columns={6} />
      ) : invoicesQuery.isError ? (
        <EmptyState
          title={
            invoicesQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.invoices.loadError')
          }
          description={
            invoicesQuery.error.status === 403
              ? t('merchant.orders.accessDeniedDescription')
              : t(resolveErrorMessageKey(invoicesQuery.error))
          }
        />
      ) : invoicesQuery.data.length === 0 ? (
        <EmptyState
          title={t('merchant.invoices.emptyTitle')}
          description={t('merchant.invoices.emptyDescription')}
        />
      ) : (
        <InvoiceTable invoices={invoicesQuery.data} />
      )}
    </div>
  );
}
